import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "./app.css";
import { MapView } from "./components/MapView";
import { Controls } from "./components/Controls";
import { HudIndicators } from "./components/HudIndicators";
import { SimulationHeader } from "./components/SimulationHeader";
import type { GpsPosition } from "./sensors/gps";
import type { HeadingData } from "./sensors/heading";
import type { MotionData } from "./sensors/motion";
import { startMotion } from "./sensors/motion";

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  }
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function App() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const [follow, setFollow] = useState(true);
  const [position, setPosition] = useState<GpsPosition | null>(null);
  const [heading, setHeading] = useState<HeadingData | null>(null);
  const [motion, setMotion] = useState<MotionData | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    const onBIP = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();        // empêche le mini-bar auto
      setDeferred(e);            // on pourra déclencher plus tard
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    // SW ok ?
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(() => setSwReady(true));
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Démarrage accéléromètre
  useEffect(() => {
    const motionController = startMotion({
      onMotion: setMotion,
      onMotionPermissionNeeded: () => {
        console.log('Permission accéléromètre requise');
      },
      frequency: 60,
      windowSize: 5
    });

    return () => {
      motionController.stop();
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") setDeferred(null);
  };

  const handleCenterClick = () => {
    window.dispatchEvent(new CustomEvent('centerOnUser'));
  };

  return (
    <div className="h-full flex flex-col">
      <SimulationHeader
        onPositionUpdate={setPosition}
        onSimulationStateChange={setIsSimulating}
        deferred={deferred}
        installed={installed}
        swReady={swReady}
        onInstall={handleInstall}
      />

      <main className="flex-1 relative">
        <MapView 
          follow={follow}
          onFollowChange={setFollow}
          onCenterRequest={handleCenterClick}
          onPositionUpdate={setPosition}
          onHeadingUpdate={setHeading}
          isSimulating={isSimulating}
          position={position}
        />
        <HudIndicators 
          position={position}
          heading={heading}
          motion={motion}
        />
        <Controls
          follow={follow}
          onFollowChange={setFollow}
          onCenterClick={handleCenterClick}
        />
      </main>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(<App />);

// Enregistre le SW (ajuste ce chemin si l'app est sous un sous-dossier)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", async () => {
    try {
      await navigator.serviceWorker.register("/sw.js");
    } catch (error) {
      console.warn('Service Worker registration failed:', error);
    }
  });
}
